/**
 * Audience editor for a `segment`-scoped Home layout.
 *
 * Renders the rule set as the plain-English sentence it means, because the
 * AND/OR split is the one thing a non-engineer will get wrong: values inside a
 * rule are OR-ed ("owner-operator OR company owner"), and rules are AND-ed
 * across fields ("...AND runs a reefer"). A checkbox grid alone does not say
 * that, so the sentence is rendered above it and updates live.
 */

import { Box, Card, Checkbox, Chip, FormControl, FormLabel, Input, Stack, Typography } from '@mui/joy'
import { DISPATCHER_POSITIONS } from '@numeo/types-core'
import { EQUIPMENT_TYPE_CODES, MobileHomeSegment, SEGMENT_RULE_FIELDS, SegmentRule, SegmentRuleField } from '@numeo/types'

/** Operator-facing labels. The stored values stay the raw slugs. */
const FIELD_LABELS: Record<SegmentRuleField, string> = {
    position: 'Job role',
    equipmentTypes: 'Equipment',
}

const FIELD_VALUES: Record<SegmentRuleField, readonly string[]> = {
    position: DISPATCHER_POSITIONS,
    equipmentTypes: EQUIPMENT_TYPE_CODES,
}

/** `owner_operator` → `Owner operator`; `dry-van` → `Dry van`. */
function humanize(value: string): string {
    const spaced = value.replace(/[-_]/g, ' ')
    return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** The rule set as the sentence it actually means. Empty → nobody. */
function describeSegment(rules: SegmentRule[]): string {
    const parts = rules
        .filter((r) => r.values.length)
        .map((r) => {
            const values = r.values.map(humanize).join(' or ')
            return r.field === 'equipmentTypes' ? `runs ${values}` : `is ${values}`
        })
    if (!parts.length) return 'No rules yet — this segment matches nobody.'
    return `Shown to users where the job role/equipment ${parts.join(' AND ')}.`
}

interface Props {
    segment: MobileHomeSegment
    onChange: (next: MobileHomeSegment) => void
    disabled?: boolean
}

export function SegmentEditor({ segment, onChange, disabled }: Props) {
    const ruleFor = (field: SegmentRuleField): SegmentRule | undefined => segment.rules.find((r) => r.field === field)

    function toggleValue(field: SegmentRuleField, value: string) {
        const existing = ruleFor(field)
        const values = existing?.values ?? []
        const nextValues = values.includes(value) ? values.filter((v) => v !== value) : [...values, value]
        // A field with nothing selected is dropped entirely rather than kept as an
        // empty rule: an empty `values` fails validation server-side, and "no
        // constraint on this field" is exactly what absence means.
        const others = segment.rules.filter((r) => r.field !== field)
        const nextRules = nextValues.length ? [...others, { field, values: nextValues }] : others
        // Keep rules in a stable declared order so the sentence does not reshuffle
        // as the operator clicks.
        nextRules.sort((a, b) => SEGMENT_RULE_FIELDS.indexOf(a.field) - SEGMENT_RULE_FIELDS.indexOf(b.field))
        onChange({ ...segment, rules: nextRules })
    }

    return (
        <Card sx={{ mb: 3 }}>
            <Typography level="title-md" sx={{ mb: 1 }}>
                Audience
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Name</FormLabel>
                    <Input
                        placeholder="e.g. Owner-operators"
                        value={segment.name}
                        disabled={disabled}
                        onChange={(e) => onChange({ ...segment, name: e.target.value })}
                    />
                </FormControl>
                <FormControl sx={{ width: { sm: 160 } }}>
                    <FormLabel>Priority</FormLabel>
                    <Input
                        type="number"
                        slotProps={{ input: { min: 0, max: 1000 } }}
                        value={segment.priority}
                        disabled={disabled}
                        onChange={(e) => onChange({ ...segment, priority: Number(e.target.value) || 0 })}
                    />
                </FormControl>
            </Stack>

            <Typography level="body-sm" sx={{ mb: 2, fontStyle: 'italic' }}>
                {describeSegment(segment.rules)}
            </Typography>

            {SEGMENT_RULE_FIELDS.map((field) => {
                const selected = ruleFor(field)?.values ?? []
                return (
                    <Box key={field} sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                            <Typography level="title-sm">{FIELD_LABELS[field]}</Typography>
                            {selected.length > 0 && (
                                <Chip size="sm" variant="soft" color="primary">
                                    {selected.length} selected
                                </Chip>
                            )}
                        </Stack>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {FIELD_VALUES[field].map((value) => (
                                <Checkbox
                                    key={value}
                                    label={humanize(value)}
                                    size="sm"
                                    disabled={disabled}
                                    checked={selected.includes(value)}
                                    onChange={() => toggleValue(field, value)}
                                />
                            ))}
                        </Box>
                    </Box>
                )
            })}

            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                Users who have not set this on their profile match no rule, so a segment always reaches a subset of the people it names.
            </Typography>
        </Card>
    )
}
